import { RouteConfig, MenuItem, BreadcrumbItem } from '../types/router';
import { AdminRole, OrganizationRole } from '../types';

/**
 * Check if user has permission to access a route
 */
export const hasRoutePermission = (
  route: RouteConfig,
  userRole: AdminRole | null,
  userOrgRole?: OrganizationRole
): boolean => {
  // Public routes are always accessible
  if (route.public) {
    return true;
  }

  // If no roles specified, route is accessible to authenticated users
  if (!route.roles && !route.organizationRoles) {
    return true;
  }

  // Check admin roles
  if (route.roles && userRole) {
    if (route.roles.includes(userRole)) {
      return true;
    }
  }

  // Check organization roles
  if (route.organizationRoles && userOrgRole) {
    if (route.organizationRoles.includes(userOrgRole)) {
      return true;
    }
  }

  return false;
};

/**
 * Generate menu items from route configuration
 */
export const generateMenuItems = (
  routes: RouteConfig[],
  userRole: AdminRole | null,
  userOrgRole?: OrganizationRole
): MenuItem[] => {
  const menuItems: MenuItem[] = [];

  routes.forEach(route => {
    // Skip routes that should be hidden in menu
    if (route.meta?.hideInMenu) {
      return;
    }

    // Skip routes user doesn't have permission for
    if (!hasRoutePermission(route, userRole, userOrgRole)) {
      return;
    }

    const menuItem: MenuItem = {
      key: route.path,
      label: route.meta?.title || route.path,
      path: route.path,
      roles: route.roles,
      organizationRoles: route.organizationRoles
    };

    // Add icon if specified
    if (route.meta?.icon) {
      menuItem.icon = route.meta.icon;
    }

    // Process children recursively
    if (route.children) {
      const childMenuItems = generateMenuItems(route.children, userRole, userOrgRole);
      if (childMenuItems.length > 0) {
        menuItem.children = childMenuItems;
      }
    }

    menuItems.push(menuItem);
  });

  // Sort by order if specified
  return menuItems.sort((a, b) => {
    const aRoute = routes.find(r => r.path === a.key);
    const bRoute = routes.find(r => r.path === b.key);
    const aOrder = aRoute?.meta?.order || 999;
    const bOrder = bRoute?.meta?.order || 999;
    return aOrder - bOrder;
  });
};

/**
 * Generate breadcrumbs from current path
 * Note: This function returns translation keys, actual translation happens in the component
 */
export const generateBreadcrumbs = (currentPath: string): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  const pathSegments = currentPath.split('/').filter(Boolean);
  
  // Define route translation key mapping
  const routeTranslationKeys: Record<string, string> = {
    'admin': 'navigation:breadcrumb.admin',
    'member': 'navigation:breadcrumb.member',
    'dashboard': 'navigation:menu.dashboard',
    'myteam': 'navigation:menu.myTeam',
    'settings': 'navigation:userMenu.settings',
    'app-accounts': 'navigation:menu.appAccounts',
    'organizations': 'navigation:menu.organizations',
    'my-organization': 'navigation:menu.myOrganization',
    'teams': 'navigation:menu.teams',
    'orders': 'navigation:menu.orders',
    'subscription-plans': 'navigation:menu.subscriptionPlans',
    'content-settings': 'navigation:menu.policySettings',
    'faq-settings': 'navigation:menu.faqSettings',
    'test-records': 'navigation:menu.testRecords',
    'report': 'pages:memberReport.breadcrumb'
  };
  
  let currentRoute = '';
  
  pathSegments.forEach((segment, index) => {
    const prevSegment = pathSegments[index - 1];
    const nextSegment = pathSegments[index + 1];
    if (prevSegment === 'test-records' && nextSegment === 'report') {
      return;
    }
    currentRoute += `/${segment}`;
    
    // Use translation key if available, otherwise use the segment as fallback
    const titleKey = routeTranslationKeys[segment] || segment;
    
    breadcrumbs.push({
      title: titleKey,
      path: currentRoute
    });
  });

  return breadcrumbs;
};

/**
 * Find route by path in route configuration
 */
export const findRouteByPath = (
  path: string,
  routes: RouteConfig[]
): RouteConfig | null => {
  for (const route of routes) {
    if (route.path === path) {
      return route;
    }
    
    if (route.children) {
      const childRoute = findRouteByPath(path, route.children);
      if (childRoute) {
        return childRoute;
      }
    }
  }
  
  return null;
};

/**
 * Get default redirect path for user role
 */
export const getDefaultRedirectPath = (userRole: AdminRole | null): string => {
  switch (userRole) {
    case 'super_admin':
      // 超级管理员默认进入 Dashboard
      return '/admin/dashboard';
    case 'admin':
      // 机构管理员默认进入 Dashboard
      return '/admin/dashboard';
    case 'member':
      return '/member/dashboard';
    case 'operator':
      return '/orders';
    default:
      return '/';
  }
};

/**
 * Check if path matches current route (supports wildcards)
 */
export const isPathActive = (currentPath: string, routePath: string): boolean => {
  if (routePath === currentPath) {
    return true;
  }
  
  // Handle parent paths (e.g., /users should be active when on /users/123)
  if (currentPath.startsWith(routePath + '/')) {
    return true;
  }
  
  return false;
};
